import type {
  HealthResponse,
  AuthStatusResponse,
  SettingsResponse,
  HoldingsResponse,
  ComposerPreviewResponse,
  StrategyForPreview,
  RebalancePlanResponse,
  EquityCurveResponse,
  EquityLatestResponse,
  RebalanceRun,
  PlacedOrder,
  BrokerAccountsResponse,
  MarketStatusResponse,
  SymphonyListResponse,
  PaperAccountResponse,
  StrategyBacktestResponse,
  TradesResponse,
  WeightAttributionResponse,
  BacktestAttributionResponse,
  BacktestHistoryResponse,
  BacktestSubmitResponse,
  BacktestStatusResponse,
  BacktestBenchmarkResponse,
  BacktestCompareResponse,
  RegimeLatestScoresResponse,
  RegimeHistoryResponse,
  RegimeConfigsResponse,
  RegimeJobsResponse,
  RegimeJobRun,
  RegimeBackfillStatus,
  BacktestVerifySubmitResponse,
  BacktestVerifyStatusResponse,
} from '../types/api';

const API_BASE = '/api';

export interface McpHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  enabled: boolean;
  endpoint: string;
  transport?: string | null;
  protocol_version?: string;
  server_name?: string;
  tool_count?: number | null;
  tools?: string[];
  required_tools_present?: boolean;
  missing_required_tools?: string[];
  latency_ms?: number | null;
  checked_at: string;
  error_type?: string | null;
  message?: string | null;
  suggested_fix?: string | null;
}

export interface McpStatusResponse {
  enable_mcp_server_env_raw: string | null;
  enable_mcp_server_effective: boolean;
  mcp_api_key_configured: boolean;
  mcp_app_built: boolean;
  mcp_mounted: boolean;
  mcp_mount_path: string | null;
  registered_routes_containing_mcp: string[];
  app_start_time: string | null;
  message: string;
}

export interface McpSelfTestResponse {
  status: 'healthy' | 'unhealthy';
  tool_count: number | null;
  tools: string[];
  latency_ms: number | null;
  endpoint: string | null;
  error: string | null;
  checked_at: string;
}

export interface McpOAuthReadinessResponse {
  base_url: string;
  protected_resource_metadata_url: string;
  authorization_server_metadata_url: string;
  authorize_endpoint: string;
  token_endpoint: string;
  mcp_url: string;
  checks: {
    protected_resource_metadata_available: boolean;
    authorization_server_metadata_available: boolean;
    authorize_endpoint_available: boolean;
    token_endpoint_available: boolean;
    mcp_oauth_implementation_ready: boolean;
    valid_oauth_token_exists: boolean;
    mcp_accepts_bearer_oauth_token: boolean;
  };
  notes: string[];
}

export interface LogFileInfo {
  filename: string;
  date: string;
  size_bytes: number;
  is_current: boolean;
}

export interface LogContentResponse {
  filename: string;
  content: string;
  line_count: number;
}

interface LoginUrlResponse {
  login_url: string;
  broker: string;
}

interface OrderHistoryResponse {
  orders: PlacedOrder[];
  total_count: number;
}

interface ExecuteResponse {
  run_id: number;
  orders_submitted: number;
  orders_filled: number;
  orders_failed: number;
  message: string;
}

// User Authentication Types
export interface UserResponse {
  id: number;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login_at?: string;
}

export interface AuthStatusCheckResponse {
  authenticated: boolean;
  user?: UserResponse;
  trading_mode?: string;
  live_confirmed?: boolean;
}

export interface MessageResponse {
  message: string;
  success?: boolean;
}

export interface InvitationResponse {
  id: number;
  email: string;
  created_at: string;
  expires_at: string;
  used_at?: string;
  revoked_at?: string;
  invited_by_email: string;
  status: string;
}

export interface InvitationListResponse {
  invitations: InvitationResponse[];
  total: number;
}

export interface ValidateInvitationResponse {
  valid: boolean;
  email?: string;
  message?: string;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include', // Include cookies for authentication
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    // Handle structured error detail (e.g., from ComposerAPIError)
    let errorMessage: string;
    if (typeof error.detail === 'object' && error.detail !== null) {
      errorMessage = error.detail.message || JSON.stringify(error.detail);
    } else {
      errorMessage = error.detail || `API error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

/** What an imported artifact looks like on the Deployed Strategies screen (SDO-6). */
export interface DeployedArtifact {
  id: number;
  name: string;
  source_strategy_id: string;
  source_version: number;
  source_mode: string;
  definition_hash: string;
  bundle_hash: string;
  dependency_count: number;
  validation_status: string | null;
  imported_at: string | null;
  imported_by: string | null;
  exported_at: string | null;
  exported_by: string | null;
  schema_version: string;
}

/** A deployment: which artifact, at what allocation, in which mode (SDO-7). */
export interface StrategyDeployment {
  id: number;
  artifact_id: number;
  source_strategy_id: string;
  name: string;
  source_version: number;
  definition_hash: string;
  bundle_hash: string;
  allocation_percent: number;
  trading_mode: string;
  is_enabled: boolean;
  account_id: string | null;
  schedule_cron: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** False when the pinned definition no longer hashes to its stored hash — it will not trade. */
  definition_intact: boolean;
}

/** One line of a deployment's version history (SDO-8). */
export interface DeploymentVersionSwitch {
  id: number;
  from_source_version: number | null;
  from_bundle_hash: string | null;
  to_source_version: number;
  to_bundle_hash: string;
  switched_at: string;
  switched_by: string | null;
  note: string | null;
}

export interface DeploymentImportResult {
  outcome: 'new' | 'already_imported';
  artifact_id: number | null;
  imported_at: string | null;
  summary: {
    schema: string;
    source_strategy_id: string;
    source_version: number;
    name: string;
    source_mode: string;
    definition_hash: string;
    bundle_hash: string;
    definition_bytes: number;
    dependencies: { key: string; name: string | null; definition_hash: string }[];
    validation_status: string;
    validated_at: string;
    exported_at: string;
    exported_by: string;
    source: string;
  };
}

/**
 * A refused import, carrying the reason as data (SDO-6).
 *
 * The operator has to tell these apart: an unknown schema means "upgrade Trade", a hash mismatch
 * means "this file was altered since it was exported", and a version conflict means Lab produced
 * two different definitions calling themselves one version — a bug to go and fix rather than
 * anything that can be resolved at this screen.
 */
export class DeploymentImportError extends Error {
  readonly reason: string | null;

  constructor(message: string, reason: string | null) {
    super(message);
    this.name = 'DeploymentImportError';
    this.reason = reason;
  }
}

/**
 * Gzip a package for upload, or send it as it is where the browser cannot.
 *
 * Production's proxy stops reading a request body after 60 s and answers 502 before Trade sees
 * anything; a real 11.8 MB export took ~60 s on a ~200 KB/s uplink. Strategy JSON compresses
 * ~24x. Trade detects gzip by its magic bytes and recomputes the hashes on the decompressed bytes,
 * so this changes how long the upload takes and nothing about what is verified.
 */
async function compressForUpload(packageFile: File): Promise<[Blob, string]> {
  if (typeof CompressionStream === 'undefined') return [packageFile, packageFile.name];
  const gzipped = await new Response(
    packageFile.stream().pipeThrough(new CompressionStream('gzip')),
  ).blob();
  return [gzipped, `${packageFile.name}.gz`];
}

/** Where a package upload is, for a screen that must never look idle while one is running. */
export type UploadProgress =
  | { phase: 'compressing' }
  | { phase: 'uploading'; loaded: number; total: number }
  | { phase: 'processing' };

/**
 * Refusals that come from in front of Trade rather than from it.
 *
 * Production's proxy answers 502 when a body is still arriving after 60 s, and 502/503/504 when the
 * app is restarting. None of those reached the importer, so showing them as "Import refused: Bad
 * Gateway" blamed the file for what the connection did.
 */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * Upload a package, reporting progress.
 *
 * `XMLHttpRequest` rather than `fetch`, deliberately: `fetch` cannot report upload progress, and an
 * upload of several megabytes over a slow link is exactly when an operator needs to see it moving.
 * Not through `fetchApi` either: that sets a JSON content type, and multipart needs the browser to
 * set its own boundary.
 */
async function uploadPackage(
  endpoint: string,
  packageFile: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<DeploymentImportResult> {
  onProgress?.({ phase: 'compressing' });
  const [body, filename] = await compressForUpload(packageFile);
  const formData = new FormData();
  formData.append('package_file', body, filename);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${endpoint}`);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.({ phase: 'uploading', loaded: event.loaded, total: event.total });
      }
    };
    // Every byte is sent; what remains is Trade parsing, hashing and validating it.
    xhr.upload.onload = () => onProgress?.({ phase: 'processing' });

    xhr.onload = () => {
      let payload: { detail?: unknown } | null = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        // A proxy error page is HTML, not JSON; handled by status below.
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload) {
        resolve(payload as unknown as DeploymentImportResult);
        return;
      }
      const detail = payload?.detail;
      if (detail && typeof detail === 'object') {
        const { message, reason } = detail as { message?: string; reason?: string };
        reject(new DeploymentImportError(message ?? 'Import refused', reason ?? null));
      } else if (GATEWAY_STATUSES.has(xhr.status)) {
        reject(new DeploymentImportError(
          `The server's proxy answered ${xhr.status} before Trade received the file. It stops ` +
            'waiting for an upload after 60 seconds, so a large file on a slow connection can run ' +
            'out of time; it also answers this way while Trade restarts. Nothing was imported. Try ' +
            'again, and if it keeps happening the proxy timeout needs raising.',
          'gateway',
        ));
      } else {
        reject(new DeploymentImportError(
          typeof detail === 'string' && detail ? detail : `API error: ${xhr.status}`,
          null,
        ));
      }
    };
    xhr.onerror = () =>
      reject(new DeploymentImportError(
        'The connection dropped before Trade answered. Nothing was imported — check the connection and try again.',
        'gateway',
      ));

    xhr.send(formData);
  });
}

export const api = {
  // Health
  getHealth: (): Promise<HealthResponse> => fetchApi('/health'),

  // Auth
  getAuthStatus: (): Promise<AuthStatusResponse> =>
    fetchApi('/broker/auth/status'),

  getLoginUrl: (): Promise<LoginUrlResponse> =>
    fetchApi('/broker/auth/login-url'),

  // Broker Accounts
  getBrokerAccounts: (): Promise<BrokerAccountsResponse> =>
    fetchApi('/broker/accounts'),

  selectBrokerAccount: (accountId: string): Promise<{ message: string; account_id: string; broker: string }> =>
    fetchApi('/broker/accounts/select', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId }),
    }),

  // Settings
  getSettings: (): Promise<SettingsResponse> => fetchApi('/settings'),

  toggleBot: (botEnabled: boolean): Promise<SettingsResponse> =>
    fetchApi('/settings/bot-toggle', {
      method: 'POST',
      body: JSON.stringify({ bot_enabled: botEnabled }),
    }),

  toggleAutoExecute: (autoExecute: boolean): Promise<SettingsResponse> =>
    fetchApi('/settings/auto-execute', {
      method: 'POST',
      body: JSON.stringify({ auto_execute: autoExecute }),
    }),

  updateModeSettings: (tradingMode: string, botEnabled?: boolean, autoExecute?: boolean): Promise<SettingsResponse> =>
    fetchApi('/settings/mode-settings', {
      method: 'POST',
      body: JSON.stringify({ trading_mode: tradingMode, bot_enabled: botEnabled, auto_execute: autoExecute }),
    }),

  triggerScheduledRun: (): Promise<{ success: boolean; message: string }> =>
    fetchApi('/trigger-scheduled-run', {
      method: 'POST',
    }),

  // Holdings
  getHoldings: (): Promise<HoldingsResponse> => fetchApi('/broker/holdings'),

  // Composer
  getComposerPreview: (): Promise<ComposerPreviewResponse> =>
    fetchApi('/composer/preview', { method: 'POST' }),

  // `deploymentId` selects one deployment's weights (its pinned version, evaluated as execution would);
  // omitted, the combined preview. `refresh` recalculates today's instead of reusing the stored one.
  getLatestComposerPreview: (deploymentId?: number, refresh: boolean = false): Promise<ComposerPreviewResponse> => {
    const params = new URLSearchParams();
    if (deploymentId !== undefined) params.set('deployment_id', String(deploymentId));
    if (refresh) params.set('refresh', 'true');
    const qs = params.toString();
    return fetchApi(qs ? `/composer/previews/latest?${qs}` : '/composer/previews/latest');
  },

  getHistoricalComposerPreview: (date: string, storedOnly: boolean = false, deploymentId?: number): Promise<ComposerPreviewResponse> => {
    const params = new URLSearchParams({ preview_date: date, stored_only: String(storedOnly) });
    if (deploymentId !== undefined) params.set('deployment_id', String(deploymentId));
    return fetchApi(`/composer/previews/history?${params.toString()}`);
  },

  getStrategiesForPreview: (): Promise<StrategyForPreview[]> =>
    fetchApi('/composer/strategies/for-preview'),

  // Rebalance
  getTodayPlan: (): Promise<RebalancePlanResponse> =>
    fetchApi('/rebalance/plan/today'),

  generatePlan: (): Promise<RebalancePlanResponse> =>
    fetchApi('/rebalance/plan', { method: 'POST' }),

  executePlan: (runId: number): Promise<ExecuteResponse> =>
    fetchApi(`/rebalance/execute?run_id=${encodeURIComponent(String(runId))}`, { method: 'POST' }),

  // Orders
  getOrders: (orderDate?: string): Promise<OrderHistoryResponse> => 
    fetchApi(orderDate ? `/orders?order_date=${encodeURIComponent(orderDate)}` : '/orders'),

  getTrades: (tradeDate?: string): Promise<TradesResponse> =>
    fetchApi(tradeDate ? `/trades?trade_date=${encodeURIComponent(tradeDate)}` : '/trades'),

  // Runs
  getRuns: (limit: number = 30, todayOnly: boolean = false): Promise<RebalanceRun[]> =>
    fetchApi(`/runs?limit=${encodeURIComponent(String(limit))}&today_only=${encodeURIComponent(String(todayOnly))}`),

  // Equity
  getEquityCurve: (start: string, end: string): Promise<EquityCurveResponse> =>
    fetchApi(`/equity-curve?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),

  getEquityLatest: (): Promise<EquityLatestResponse> => fetchApi('/equity-latest'),

  takeSnapshot: (): Promise<{ id: number; equity: number; cash: number; timestamp: string }> =>
    fetchApi('/equity-snapshot', { method: 'POST' }),

  // User Authentication
  requestOTP: (email: string): Promise<MessageResponse> =>
    fetchApi('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyOTP: (
    email: string,
    otp: string,
    trading_mode?: string,
    live_confirmed?: boolean
  ): Promise<UserResponse> =>
    fetchApi('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        email,
        otp,
        trading_mode: trading_mode || 'PAPER',
        live_confirmed: live_confirmed || false
      }),
    }),

  logout: (): Promise<MessageResponse> =>
    fetchApi('/auth/logout', { method: 'POST' }),

  getMe: (): Promise<AuthStatusCheckResponse> =>
    fetchApi('/auth/me'),

  validateInvitation: (token: string): Promise<ValidateInvitationResponse> =>
    fetchApi('/auth/validate-invitation', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  // Admin - Invitations
  inviteUser: (email: string): Promise<MessageResponse> =>
    fetchApi('/admin/invitations', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getInvitations: (): Promise<InvitationListResponse> =>
    fetchApi('/admin/invitations'),

  revokeInvitation: (invitationId: number): Promise<MessageResponse> =>
    fetchApi(`/admin/invitations/${invitationId}/revoke`, {
      method: 'POST',
    }),

  resendInvitation: (invitationId: number): Promise<MessageResponse> =>
    fetchApi(`/admin/invitations/${invitationId}/resend`, {
      method: 'POST',
    }),

  // Market Status
  getMarketStatus: (): Promise<MarketStatusResponse> =>
    fetchApi('/market/status'),

  // Symphony/Strategy Management — DEPLOYMENT AND OPERATIONS ONLY (SDO-3).
  //
  // Eight authoring methods and one research method were removed here: addSymphony,
  // validateSymphony, validateSymphonyDsl, validatePortfolioJson, validatePortfolioJsonText,
  // revalidatePortfolioDeps, getPortfolioDependencies, deleteSymphony and
  // getSymphonyTargetWeights. Authoring belongs to Lab (PB-D2), which serves its own routes
  // through `src/lab/labStrategyApi.ts`.
  //
  // Removed from the CLIENT, not merely from the page. A method left here is a method the next
  // Trade page can call in one line; deleting it makes re-adding authoring to Trade a compile
  // error rather than something a reviewer has to notice. Same enforcement SDO-1 used to keep
  // allocation unwritable from Lab.
  //
  // The backend routes still exist, marked deprecated: until SDO-6 lands there is no other way to
  // get a strategy into a Trade deployment, and removing them would leave a fresh install with no
  // path at all. See backend/app/api/strategy_routes.py.
  getSymphonies: (): Promise<SymphonyListResponse> =>
    fetchApi('/symphonies'),

  getSymphonyRegimeBackfillStatus: (symphonyId: number): Promise<RegimeBackfillStatus> =>
    fetchApi(`/symphonies/${symphonyId}/regime-backfill-status`),

  triggerSymphonyRegimeBackfill: (symphonyId: number): Promise<{ message: string; symphony_id: number }> =>
    fetchApi(`/symphonies/${symphonyId}/regime-backfill`, { method: 'POST' }),

  // `updateSymphonyAllocation` is gone on purpose. Since SDO-9 the rebalancer trades deployments
  // (`updateDeployment`), and a positive `user_symphonies` allocation traded nothing — except that the
  // boot-time cutover migration turned it into an ENABLED deployment. The route now refuses values
  // above 0; removing the method makes re-adding the control a compile error.

  // `getSymphonyTargetWeights` (the per-strategy weight preview) was removed at SDO-3 as the one
  // RESEARCH call on the old page. `getCombinedTargetWeights` stays: it is what the whole
  // deployment will trade, which is Trade's Preview page, not research on one strategy.
  getCombinedTargetWeights: (): Promise<ComposerPreviewResponse> =>
    fetchApi('/symphonies/combined-target-weights'),

  // Deployment packages (SDO-6) — the receiving half of the boundary.
  //
  // This is an upload, and SDO-3 removed every other upload from Trade on purpose, so the
  // distinction matters: what these accept is a *deployment package* whose hashes are recomputed
  // and whose definition is validated through the engine that will execute it. A raw DSL or
  // portfolio JSON — a definition Trade would have to trust — is exactly what is no longer
  // accepted. Preview and import are separate calls because preview must write nothing.
  previewDeploymentPackage: (
    packageFile: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<DeploymentImportResult> =>
    uploadPackage('/deployments/preview', packageFile, onProgress),

  importDeploymentPackage: (
    packageFile: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<DeploymentImportResult> =>
    uploadPackage('/deployments/import', packageFile, onProgress),

  getDeployedArtifacts: (): Promise<DeployedArtifact[]> =>
    fetchApi('/deployments/artifacts'),

  // Deployments (SDO-7) — an artifact plus how it executes. Creating one takes the artifact and
  // the account and nothing else: allocation, mode and enabled are absent from the body on
  // purpose, so a deployment cannot be created armed. Configuring is a separate call.
  getDeployments: (): Promise<StrategyDeployment[]> =>
    fetchApi('/deployments'),

  createDeployment: (artifactId: number, accountId?: string): Promise<StrategyDeployment> =>
    fetchApi('/deployments', {
      method: 'POST',
      body: JSON.stringify({ artifact_id: artifactId, account_id: accountId ?? null }),
    }),

  updateDeployment: (
    deploymentId: number,
    patch: { allocation_percent?: number; trading_mode?: string; is_enabled?: boolean },
  ): Promise<StrategyDeployment> =>
    fetchApi(`/deployments/${deploymentId}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  retireDeployment: (deploymentId: number): Promise<{ id: number; is_active: boolean }> =>
    fetchApi(`/deployments/${deploymentId}`, { method: 'DELETE' }),

  // Version switching (SDO-8) — its own call, not a field on updateDeployment. It changes WHAT
  // executes rather than how much of it, and folding it into the settings patch would make a
  // version switch indistinguishable from an allocation edit in every log this system keeps.
  switchDeploymentVersion: (
    deploymentId: number,
    artifactId: number,
    note?: string,
  ): Promise<StrategyDeployment> =>
    fetchApi(`/deployments/${deploymentId}/switch`, {
      method: 'POST',
      body: JSON.stringify({ artifact_id: artifactId, note: note ?? null }),
    }),

  getDeploymentHistory: (deploymentId: number): Promise<DeploymentVersionSwitch[]> =>
    fetchApi(`/deployments/${deploymentId}/history`),

  // Paper Trading
  getPaperAccount: (): Promise<PaperAccountResponse> =>
    fetchApi('/paper/account'),

  resetPaperAccount: (startingCash?: number): Promise<MessageResponse> =>
    fetchApi('/paper/reset', {
      method: 'POST',
      body: JSON.stringify(startingCash !== undefined ? { starting_cash: startingCash } : {}),
    }),

  // Broker connection
  resetBrokerConnection: (): Promise<MessageResponse> =>
    fetchApi('/broker/reset', { method: 'POST' }),

  // Weight Attribution
  getWeightAttribution: (date?: string): Promise<WeightAttributionResponse> =>
    fetchApi(date ? `/weight-attribution?date=${encodeURIComponent(date)}` : '/weight-attribution'),

  runBacktestAttribution: (startDate: string, endDate: string, startingCapital?: number, symphonyId?: string): Promise<BacktestAttributionResponse> =>
    fetchApi('/weight-attribution/backtest', {
      method: 'POST',
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate,
        starting_capital: startingCapital || 100000,
        symphony_id: symphonyId || null,
      }),
    }),

  // Backtest
  runBacktest: (startDate: string, endDate: string, startingCapital?: number, symphonyId?: string, signal?: AbortSignal): Promise<StrategyBacktestResponse> =>
    fetchApi('/backtest/run', {
      method: 'POST',
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate,
        starting_capital: startingCapital || 100000,
        symphony_id: symphonyId || null,
      }),
      signal,
    }),

  submitBacktest: (
    startDate: string,
    endDate: string,
    startingCapital?: number,
    symphonyId?: string,
    options?: {
      apply_regime_multiplier?: boolean;
      regime_multiplier_policy_id?: string | null;
      compare_to_baseline?: boolean;
    },
  ): Promise<BacktestSubmitResponse> =>
    fetchApi('/backtest/submit', {
      method: 'POST',
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate,
        starting_capital: startingCapital || 100000,
        symphony_id: symphonyId || null,
        apply_regime_multiplier: options?.apply_regime_multiplier ?? false,
        regime_multiplier_policy_id: options?.regime_multiplier_policy_id ?? null,
        compare_to_baseline: options?.compare_to_baseline ?? false,
      }),
    }),

  getBacktestStatus: (runId: number): Promise<BacktestStatusResponse> =>
    fetchApi(`/backtest/status/${runId}`),

  getBacktestHistory: (): Promise<BacktestHistoryResponse> =>
    fetchApi('/backtest/history'),

  getBacktestResult: (runId: number): Promise<StrategyBacktestResponse> =>
    fetchApi(`/backtest/result/${runId}`),

  getDynamicSelectExplanation: (runId: number): Promise<import('../types/api').DynamicSelectExplanationResponse> =>
    fetchApi(`/backtest/result/${runId}/dynamic-select-explanation`),
  getDynamicSelectExplanationCsvUrl: (runId: number): string =>
    `/api/backtest/result/${runId}/dynamic-select-explanation.csv`,

  getBacktestBenchmark: (symbol: string, startDate: string, endDate: string, startingCapital: number): Promise<BacktestBenchmarkResponse> =>
    fetchApi(`/backtest/benchmark?symbol=${encodeURIComponent(symbol)}&start_date=${startDate}&end_date=${endDate}&starting_capital=${startingCapital}`),

  getBacktestCompare: (symphonyId: string, startDate: string, endDate: string, startingCapital: number): Promise<BacktestCompareResponse> =>
    fetchApi(`/backtest/compare?symphony_id=${encodeURIComponent(symphonyId)}&start_date=${startDate}&end_date=${endDate}&starting_capital=${startingCapital}`),

  submitVerification: (params: {
    start_date: string;
    end_date: string;
    starting_capital: number;
    symphony_id?: string;
    scenario_matrix?: string[];
    thresholds?: Record<string, number | boolean>;
    missing_data_policy?: string;
  }): Promise<BacktestVerifySubmitResponse> =>
    fetchApi('/backtest/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    }),

  getVerificationStatus: (runId: number): Promise<BacktestVerifyStatusResponse> =>
    fetchApi(`/backtest/verify/${runId}`),

  getVerificationExportUrl: (runId: number): string =>
    `/api/backtest/verify/${runId}/export.csv`,

  getVerificationDataQuality: (runId: number): Promise<import('../types/api').DataQualityResponse> =>
    fetchApi(`/backtest/verify/${runId}/data-quality`),

  getVerificationCacheDiagnostics: (runId: number): Promise<import('../types/api').VerifyCacheDiagnostics> =>
    fetchApi(`/backtest/verify/${runId}/cache-diagnostics`),

  submitWalkForward: (params: {
    start_date: string;
    end_date: string;
    starting_capital: number;
    symphony_id?: string;
    split_date?: string;
    custom_stress_periods?: { label?: string; start_date: string; end_date: string }[];
  }): Promise<import('../types/api').WalkForwardSubmitResponse> =>
    fetchApi('/backtest/walkforward/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    }),

  getWalkForwardStatus: (runId: string): Promise<import('../types/api').WalkForwardStatusResponse> =>
    fetchApi(`/backtest/walkforward/status/${encodeURIComponent(runId)}`),

  // Admin MCP Health
  testMcpHealth: (): Promise<McpHealthResponse> =>
    fetchApi('/admin/mcp/health'),

  // MCP Status & Self-Test (no-auth endpoints from Task #103)
  getMcpStatus: (): Promise<McpStatusResponse> =>
    fetchApi('/mcp/status'),

  postMcpSelfTest: (): Promise<McpSelfTestResponse> =>
    fetchApi('/mcp/self-test', { method: 'POST' }),

  // OAuth readiness (Task #106)
  getMcpOAuthReadiness: (): Promise<McpOAuthReadinessResponse> =>
    fetchApi('/mcp/oauth-readiness'),

  // Admin Logs
  getLogFiles: (): Promise<{ files: LogFileInfo[]; total: number }> =>
    fetchApi('/admin/logs/files'),

  getLogContent: (filename: string, lines?: number, search?: string): Promise<LogContentResponse> => {
    const params = new URLSearchParams({ filename });
    if (lines) params.set('lines', String(lines));
    if (search) params.set('search', search);
    return fetchApi(`/admin/logs/content?${params.toString()}`);
  },

  triggerLogCleanup: (): Promise<{ deleted_files: string[]; count: number }> =>
    fetchApi('/admin/logs/cleanup', { method: 'POST' }),

  // Cache Admin
  getCacheStatus: (): Promise<unknown> =>
    fetchApi('/cache/status'),

  cacheRefreshPrices: (): Promise<{ message: string }> =>
    fetchApi('/cache/refresh-prices', { method: 'POST' }),

  cacheRebuildWeights: (symphonyDbId: number): Promise<{ message: string }> =>
    fetchApi(`/cache/rebuild-weights/${symphonyDbId}`, { method: 'POST' }),

  cacheFillHistoryStart: (
    symphonyDbId: number,
    fillFrom: string | null,
    chunkDays = 90,
  ): Promise<{ job_id: string; status: string }> => {
    const params = new URLSearchParams({ chunk_days: String(chunkDays) });
    if (fillFrom) params.set('fill_from', fillFrom);
    return fetchApi(`/cache/fill-history/${symphonyDbId}?${params}`, { method: 'POST' });
  },

  cacheFillHistoryPoll: (jobId: string): Promise<{
    status: 'running' | 'complete' | 'failed';
    chunks_computed?: number;
    chunks_skipped?: number;
    days_filled?: number;
    current_from?: string | null;
    current_to?: string | null;
    next_fill_from?: string | null;
    done?: boolean;
    error?: string;
  }> => fetchApi(`/cache/fill-history/job/${jobId}`),

  cacheFillHistoryActive: (symphonyDbId: number): Promise<{
    active: boolean;
    job_id: string | null;
    status?: 'running' | 'complete' | 'failed';
    chunks_computed?: number;
    chunks_skipped?: number;
    days_filled?: number;
    current_from?: string | null;
    done?: boolean;
  }> => fetchApi(`/cache/fill-history/${symphonyDbId}/active`),

  cacheInvalidate: (symphonyDbId: number): Promise<{ message: string }> =>
    fetchApi(`/cache/invalidate/${symphonyDbId}`, { method: 'POST' }),

  cacheClearResults: (): Promise<{ message: string; count: number }> =>
    fetchApi('/cache/results', { method: 'DELETE' }),

  cacheCleanupOrphaned: (): Promise<{
    message: string;
    versions_deleted: number;
    weight_rows_deleted: number;
    indicator_rows_deleted: number;
    event_rows_deleted: number;
    backtest_result_rows_deleted: number;
  }> => fetchApi('/cache/cleanup-orphaned', { method: 'POST' }),

  // Cache warm-up
  getCacheWarmupStatus: (): Promise<WarmupStatus> =>
    fetchApi('/admin/cache/status'),

  triggerCacheWarmup: (): Promise<{ status: string }> =>
    fetchApi('/admin/cache/warmup', { method: 'POST' }),

  // Plan cache warm-up progress (warmup_plan_cache in ast_cache.py)
  getPlanCacheWarmupStatus: (): Promise<PlanCacheWarmupStatus> =>
    fetchApi('/cache/warmup-status'),

  triggerPlanCacheWarmup: (): Promise<{ message: string }> =>
    fetchApi('/cache/warmup-plan', { method: 'POST' }),

  getCacheRebuildStatus: (): Promise<RebuildStatus> =>
    fetchApi('/admin/cache/rebuild/status'),

  triggerCacheRebuild: (): Promise<{ status: string }> =>
    fetchApi('/admin/cache/rebuild', { method: 'POST' }),

  // Scheduler jobs
  getSchedulerJobs: (): Promise<SchedulerJobsResponse> =>
    fetchApi('/admin/scheduler/jobs'),

  // Strategy evaluation performance
  getPerfStrategies: (): Promise<PerfStrategiesResponse> =>
    fetchApi('/admin/performance/strategies'),

  getPerfStrategyDetail: (hash: string): Promise<PerfStrategyDetail> =>
    fetchApi(`/admin/performance/strategy/${hash}`),

  // Regime Scores
  getRegimeLatestScores: (params?: {
    config_id?: string;
    strategy_id?: number;
    score_type?: string;
  }): Promise<RegimeLatestScoresResponse> => {
    const qs = new URLSearchParams();
    if (params?.config_id) qs.set('config_id', params.config_id);
    if (params?.strategy_id != null) qs.set('strategy_id', String(params.strategy_id));
    if (params?.score_type) qs.set('score_type', params.score_type);
    const query = qs.toString();
    return fetchApi(`/regime/scores/latest${query ? `?${query}` : ''}`);
  },

  getRegimeScoreHistory: (params: {
    symbol: string;
    score_type?: string;
    config_id?: string;
    limit?: number;
  }): Promise<RegimeHistoryResponse> => {
    const qs = new URLSearchParams({ symbol: params.symbol });
    if (params.score_type) qs.set('score_type', params.score_type);
    if (params.config_id) qs.set('config_id', params.config_id);
    if (params.limit != null) qs.set('limit', String(params.limit));
    return fetchApi(`/regime/scores/history?${qs.toString()}`);
  },

  getRegimeConfigs: (): Promise<RegimeConfigsResponse> =>
    fetchApi('/regime/configs'),

  getRegimeJobs: (params?: {
    job_type?: string;
    status?: string;
    limit?: number;
  }): Promise<RegimeJobsResponse> => {
    const qs = new URLSearchParams();
    if (params?.job_type) qs.set('job_type', params.job_type);
    if (params?.status) qs.set('status', params.status);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return fetchApi(`/regime/jobs${query ? `?${query}` : ''}`);
  },

  getRegimeJob: (jobId: number): Promise<RegimeJobRun> =>
    fetchApi(`/regime/jobs/${jobId}`),

  getRegimeDashboardStatus: (): Promise<{
    engine_enabled: boolean;
    dashboard_enabled: boolean;
    intraday_enabled: boolean;
  }> => fetchApi('/regime/dashboard-status'),

  triggerRegimeEodNow: (apiKey: string): Promise<{ job_id: number; status: string; message: string }> =>
    fetchApi('/regime/run-eod-now', { method: 'POST', headers: { 'X-API-KEY': apiKey } }),

  triggerRegimeIntradayNow: (apiKey: string): Promise<{ job_id: number; status: string; message: string }> =>
    fetchApi('/regime/run-intraday-now', { method: 'POST', headers: { 'X-API-KEY': apiKey } }),

  triggerRegimeFinalScoreNow: (apiKey: string): Promise<{ job_id: number; status: string; message: string }> =>
    fetchApi('/regime/run-final-score-now', { method: 'POST', headers: { 'X-API-KEY': apiKey } }),

  // `getSymphoniesBySymbol` was removed at SDO-3. SDO-0 classifies `/symphonies/by-symbol` as
  // RESEARCH — "which strategies hold this symbol" is a question asked while choosing a strategy,
  // not while running one — and no Trade page called it.

  // Regime Cache Admin
  getRegimeCacheStatus: (params?: {
    start_date?: string;
    end_date?: string;
    config_id?: string;
    symbol?: string;
    years?: number;
  }): Promise<import('../types/api').RegimeCacheStatusResponse> => {
    const qs = new URLSearchParams();
    if (params?.start_date) qs.set('start_date', params.start_date);
    if (params?.end_date) qs.set('end_date', params.end_date);
    if (params?.config_id) qs.set('config_id', params.config_id);
    if (params?.symbol) qs.set('symbol', params.symbol);
    if (params?.years != null) qs.set('years', String(params.years));
    const q = qs.toString();
    return fetchApi(`/cache/regime/status${q ? `?${q}` : ''}`);
  },

  triggerRegimeBackfill: (payload: {
    start_date?: string;
    end_date?: string;
    config_id?: string;
    symbols?: string[] | null;
    force_rebuild?: boolean;
  }): Promise<{ message: string; start_date: string; end_date: string; force_rebuild: boolean }> =>
    fetchApi('/cache/regime/backfill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  getRegimeBackfillStatus: (): Promise<import('../types/api').RegimeCacheBackfillState> =>
    fetchApi('/cache/regime/backfill/status'),

  triggerRegimeEodJob: (): Promise<{ message: string }> =>
    fetchApi('/cache/regime/run-eod', { method: 'POST' }),

  // ---------------------------------------------------------------------------
  // Regime Multiplier API
  // ---------------------------------------------------------------------------

  getRegimeMultiplierPolicies: (): Promise<import('../types/api').RegimeMultiplierPoliciesResponse> =>
    fetchApi('/regime-multipliers/policies'),

  createRegimeMultiplierPolicy: (body: {
    policy_id: string;
    name: string;
    description?: string;
    bullish_factor?: number;
    neutral_factor?: number;
    bearish_factor?: number;
    min_multiplier?: number;
    max_multiplier?: number;
    normalize_weights?: boolean;
    cash_fallback_symbol?: string;
    asset_type_overrides?: Record<string, unknown>;
    symbol_overrides?: Record<string, unknown>;
  }): Promise<import('../types/api').RegimePolicyMutationResponse> =>
    fetchApi('/regime-multipliers/policies', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  updateRegimeMultiplierPolicy: (policyId: string, body: Partial<{
    name: string;
    description: string;
    bullish_factor: number;
    neutral_factor: number;
    bearish_factor: number;
    min_multiplier: number;
    max_multiplier: number;
    normalize_weights: boolean;
    cash_fallback_symbol: string;
    asset_type_overrides: Record<string, unknown>;
    symbol_overrides: Record<string, unknown>;
  }>): Promise<import('../types/api').RegimePolicyMutationResponse> =>
    fetchApi(`/regime-multipliers/policies/${policyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  getRegimeMultiplierAssets: (policyId?: string, params?: {
    as_of_date?: string;
    symbols?: string[];
  }): Promise<import('../types/api').RegimeMultiplierAssetsResponse> => {
    const qs = new URLSearchParams();
    if (policyId) qs.set('policy_id', policyId);
    if (params?.as_of_date) qs.set('as_of_date', params.as_of_date);
    if (params?.symbols?.length) qs.set('symbols', params.symbols.join(','));
    const q = qs.toString();
    return fetchApi(`/regime-multipliers/assets${q ? `?${q}` : ''}`);
  },

  getStrategyRegimeMultiplierSettings: (strategyId: number): Promise<import('../types/api').StrategyRegimeMultiplierSettings> =>
    fetchApi(`/strategies/${strategyId}/regime-multiplier-settings`),

  upsertStrategyRegimeMultiplierSettings: (strategyId: number, body: {
    policy_id?: string | number | null;
    is_enabled: boolean;
    apply_to_backtest?: boolean;
    apply_to_live_execution?: boolean;
    override_json?: Record<string, unknown>;
  }): Promise<import('../types/api').StrategyRegimeMultiplierSettings> =>
    fetchApi(`/strategies/${strategyId}/regime-multiplier-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

};

export interface WarmupStatus {
  ran_at: string | null;
  symbol_count: number | null;
  total_rows: number | null;
  symbols: string[];
  success: boolean | null;
  error: string | null;
  is_running?: boolean;
}

export interface PlanCacheWarmupStatus {
  is_running: boolean;
  started_at: string | null;
  finished_at: string | null;
  strategies_total: number;
  strategies_processed: number;
  strategies_ok: number;
  strategies_failed: number;
  elapsed_ms: number | null;
  current_symphony_id: string | null;
  last_error: string | null;
}

export interface RebuildStatus {
  ran_at: string | null;
  users_processed: number | null;
  strategies_processed: number | null;
  weight_dates_filled: number | null;
  backtests_cached: number | null;
  errors: string[];
  success: boolean | null;
  error: string | null;
  is_running?: boolean;
}

export interface SchedulerJob {
  id: string;
  name: string;
  trigger: string;
  next_run_time: string | null;
  last_run_at: string | null;
  last_run_success: boolean | null;
  last_run_detail: string | null;
}

export interface SchedulerJobsResponse {
  scheduler_running: boolean;
  is_leader: boolean;
  instance_id: string;
  jobs: SchedulerJob[];
}

export interface PerfCacheLayerStats {
  name: string;
  entries: number;
  max_entries: number;
  hits: number;
  misses: number;
  hit_rate: number;
}

export interface PerfPlanCacheStats {
  ast_cache: PerfCacheLayerStats;
  discovery_cache: PerfCacheLayerStats;
  plan_cache: PerfCacheLayerStats;
}

export interface PerfSubtreeStats {
  total_nodes?: number;
  unique_nodes?: number;
  duplicate_nodes?: number;
  duplication_ratio?: number;
  top_repeated?: { key_prefix: string; count: number }[];
}

export interface PerfRecord {
  timestamp?: string;
  ran_at?: string;
  parse_ms?: number;
  discovery_ms?: number;
  price_fetch_ms?: number;
  ast_eval_ms?: number;
  polars_collect_ms?: number;
  total_ms?: number;
  n_tickers?: number;
  n_dates?: number;
  n_rows?: number;
  subtree_stats?: PerfSubtreeStats;
}

export interface PerfDbBacktestRun {
  cache_key?: string;
  strategy_version_id?: number;
  start_date?: string;
  end_date?: string;
  starting_capital?: number;
  is_stale?: boolean;
  hit_count?: number;
  created_at?: string;
  last_accessed_at?: string;
}

export interface PerfStrategiesResponse {
  strategy_hashes: string[];
  plan_cache_stats: PerfPlanCacheStats;
}

export interface PerfStrategyDetail {
  strategy_hash: string;
  record_count: number;
  records: PerfRecord[];
  plan_cache_stats: PerfPlanCacheStats;
  db_backtest_runs: PerfDbBacktestRun[];
}
